﻿import { useEffect, useState } from "react";
import { Col, Alert } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { format, validate } from "rut.js";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import { formatMiles, hasPermission, letterInString } from "@/services/Utils";
import { useOrderDeliveryCreate } from "@/hooks/purchases/orders_delivery";
import { textConstants } from "@/languages/textConstants";
import PurchasePreview from "@/components/Purchase/PurchasePreview";

import classes from "./createOrderDelivery.module.scss";

const CreateOrderDeliveryButton = ({ params, base, data, setData, save, isFetchingDetail, checkDetailOC, _setAlerts }) => {
	let location = useLocation();
	const navigate = useNavigate();

	const { customer } = useSelector((state) => state.auth.user);

	const { data: response } = useOrderDeliveryCreate(params);
	const { permissions } = response;

	let textTitle = textConstants[customer.country_id].delivery;
	if (location.pathname.includes("sin_documento")) {
		textTitle = "Documento";
	}

	const [messagesEdit, setMessagesEdit] = useState({});
	const [messagesDelete, setMessagesDelete] = useState({});
	const [showPreview, setShowPreview] = useState(false);

	const validSave = () => {
		if (!data.business_id) {
			toast.error("Debe seleccionar razón social para continuar");
			return false;
		}

		if (!data.estate_id) {
			toast.error(
				`Debe seleccionar ${
					customer.customer_screens.find((cs) => cs.screen_id === 51) ? customer.customer_screens.find((cs) => cs.screen_id === 51)?.name : "Campo"
				} para continuar`,
			);
			return false;
		}

		if (data.rut_dealer) {
			// To-Do: validar esto para clientes extranjeros
			if (customer.country_id !== 1) {
				setData({
					...data,
					rut_dealer: "",
				});
			} else {
				if (!validate(format(data.rut_dealer))) {
					toast.error("Debe ingresar un rut válido para continuar.");
					return false;
				}
			}
		}

		if (!data.type_currency_id) {
			toast.error("Debe seleccionar la moneda para continuar");
			return false;
		}

		if (data && data.items.length === 0) {
			toast.error("Debe ingresar el menos un producto para guardar");
			return false;
		}

		let valid_item = true;
		let detailMessage = "";
		let message = "";
		data.items.forEach((item, i) => {
			if (!item.product_id) {
				message = "Debe seleccionar un producto en todos los items";
				valid_item = false;
				return;
			}

			if (!item.quantity || Number(item.quantity) === 0) {
				message = "Debe ingresar la cantidad de envases en todos los items";
				valid_item = false;
				return;
			}

			if (!item.container_content || Number(item.container_content) === 0) {
				message = "Debe ingresar el contenido en todos los items";
				valid_item = false;
				return;
			}

			if (letterInString(".", item.container_content) > 1) {
				message = "Debe ingresar un formato válido en el campo contenido envase para el item" + (i + 1);
				valid_item = false;
				return;
			}

			if (!item.value_unit_measurement || !item.unit_measurement) {
				message = "Debe seleccionar la unidad de medida en todos los items";
				valid_item = false;
				return;
			}

			if (item.price_total) {
				if (!data.type_currency_id) {
					message = "Debe seleccionar el tipo de moneda";
					valid_item = false;
					return;
				}
			}

			if (item.quantity_lot && item.save_as) {
				message = "No es posible cargar en un producto genérico al tener distribución por lotes.";
				valid_item = false;
				return;
			}

			if (item.quantity_sale && Number(item.quantity_sale) < Number(item.quantity)) {
				message = `La cantidad de envases de la ${textTitle} no puede ser menor a la cantidad restante en la factura (${formatMiles(
					Number(item.quantity_sale),
				)} envases).`;
				valid_item = false;
				return;
			}

			if (
				Object.prototype.hasOwnProperty.call(item, "save_as_original") &&
				item.save_as_original &&
				item.save_as_original !== "" &&
				Number(item.save_as_original) !== Number(item.save_as)
			) {
				message = `El producto genérico no puede ser modificado en el item ${i + 1} debido a que ya fue guardado en la factura.`;
				valid_item = false;
				return;
			}

			// const container_content_number = String(item.container_content).replace(",", ".");

			// if (
			// 	Object.prototype.hasOwnProperty.call(item, "initial_container_content") &&
			// 	Object.prototype.hasOwnProperty.call(item, "quantity_base") &&
			// 	item.initial_container_content * item.quantity_base !== container_content_number * item.quantity
			// ) {
			// 	message = `El contenido total del producto "${item.tradename}" no coincide con el contenido inicial. Revise el item.`;
			// 	valid_item = false;
			// 	return;
			// }
		});

		if (detailMessage !== "") {
			toast.error(detailMessage);
			return false;
		} else if (!valid_item) {
			toast.error(message);
			return false;
		}

		return true;
	};

	const handleSave = async () => {
		if (validSave()) {
			let newItems = await checkDetailOC();

			save(
				{ params: { ...data, items: newItems, createTags: true, location: location.pathname }, base: base },
				{
					onSuccess: (_response) => {
						if (location.pathname.includes("sin_documento")) {
							navigate("/sin_documento");
						} else {
							navigate("/guias_despacho");
						}
					},
				},
			);
		}
	};

	const handleConfirm = () => {
		handleSave();
	};

	useEffect(() => {
		const validateItems = () => {
			const newMessagesEdit = {};
			const newMessagesDelete = {};

			data.items.forEach((item) => {
				const { id, tradename, quantity_base, quantity, container_content_inventories, remaining_container_content, save_as } = item;

				if (
					quantity_base &&
					quantity_base !== 0 &&
					parseFloat(quantity) !== parseFloat(quantity_base) &&
					parseFloat(container_content_inventories) === parseFloat(remaining_container_content)
				) {
					if (save_as && save_as !== "") {
						newMessagesEdit[id] =
							`Al editar la cantidad de envases del producto "${tradename}", se modificará el contenido total del producto genérico.`;
					} else {
						newMessagesEdit[id] =
							`Al editar la cantidad de envases del producto "${tradename}", se eliminarán las etiquetas y deberá imprimir nuevamente.`;
					}
				}

				if (
					quantity_base &&
					quantity_base !== 0 &&
					parseFloat(quantity) !== parseFloat(quantity_base) &&
					parseFloat(container_content_inventories) > parseFloat(remaining_container_content)
				) {
					newMessagesDelete[id] = `No se puede modificar la cantidad de envases del producto "${tradename}" ya que tiene aplicaciones asociadas.`;
				}
			});

			setMessagesEdit(newMessagesEdit);
			setMessagesDelete(newMessagesDelete);
		};

		validateItems();
	}, [data.items]);

	return (
		<Col md={12} className={classes.col_base}>
			{messagesDelete && Object.keys(messagesDelete).length > 0 && (
				<Col md={12} xs={12}>
					<div>
						{Object.keys(messagesDelete).map((key) => (
							<Alert key={key} variant="danger" className="mb-2">
								{messagesDelete[key]}
							</Alert>
						))}
					</div>
				</Col>
			)}

			{!data.pending_tags && messagesEdit && Object.keys(messagesEdit).length > 0 && (
				<Col md={12} xs={12}>
					<div>
						{Object.keys(messagesEdit).map((key) => (
							<Alert key={key} variant="warning" className="mb-2">
								{messagesEdit[key]}
							</Alert>
						))}
					</div>
				</Col>
			)}

			<Col className={classes.buttons} md={12} xs={12}>
				<div className={classes.buttonClose}>
					<div onClick={() => (navigate(-1) ? navigate(-1) : navigate("/guias_despacho"))}>CERRAR</div>
				</div>

				{isEmpty(messagesDelete) && data && (hasPermission("edit", permissions) || hasPermission("create", permissions)) && (
					<div className={`${classes.buttonSave} ${isFetchingDetail && classes.disabled}`}>
						<div onClick={() => (isFetchingDetail ? null : validSave() && setShowPreview(true))}>
							{isFetchingDetail ? "CARGANDO..." : "GUARDAR"}{" "}
						</div>
					</div>
				)}
			</Col>

			<PurchasePreview type={"delivery"} show={showPreview} setShow={setShowPreview} data={data} handleConfirm={handleConfirm} />
		</Col>
	);
};

export default CreateOrderDeliveryButton;
